import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  canAccess,
  canView as canViewSet,
  canEdit as canEditSet,
  canFull as canFullSet,
  hasAnyAccessibleModule,
  type PermissionRequirement,
  type PermissionSet,
} from '@helios/authz';
import { useAuth } from './useAuth';
import { loadPermissions } from './permissions-loader';

export type PermissionStatus = 'loading' | 'ready' | 'error';

export interface PermissionContextValue {
  status: PermissionStatus;
  permissions: PermissionSet;
  loadedRoles: string[];
  missingRoles: string[];
  error: string | null;
  /** True once permissions are ready and grant at least one accessible module. */
  hasAccess: boolean;
  canAccess: (req: PermissionRequirement) => boolean;
  canView: (module: string, subModule: string) => boolean;
  canEdit: (module: string, subModule: string) => boolean;
  canFull: (module: string, subModule: string) => boolean;
  reload: () => void;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

/**
 * Loads the current user's merged Firestore permissions and exposes helpers.
 *
 * Fails closed: while loading, `status === 'loading'` and every check returns
 * false; on error, `status === 'error'` with an empty set. The consumer
 * should hold rendering of gated UI until `status === 'ready'` to avoid
 * flashing unauthorized modules.
 */
export function PermissionProvider({ children }: { children: ReactNode }) {
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const roles = useMemo(() => (orgId ? (claims?.orgs?.[orgId]?.roles ?? []) : []), [claims, orgId]);
  // Stable key so the effect only refetches when the actual roles change.
  const rolesKey = roles.join(',');

  const [state, setState] = useState<{
    status: PermissionStatus;
    permissions: PermissionSet;
    loadedRoles: string[];
    missingRoles: string[];
    error: string | null;
  }>({ status: 'loading', permissions: {}, loadedRoles: [], missingRoles: [], error: null });

  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    loadPermissions(orgId, roles)
      .then((result) => {
        if (cancelled) return;
        setState({
          status: 'ready',
          permissions: result.permissions,
          loadedRoles: result.loadedRoles,
          missingRoles: result.missingRoles,
          error: null,
        });
        // Surface the resolved permission set after login for debugging.
        logPermissions(orgId, roles, result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load permissions';
        // Fail closed: no permissions on error.
        setState({
          status: 'error',
          permissions: {},
          loadedRoles: [],
          missingRoles: [...roles],
          error: message,
        });
      });

    return () => {
      cancelled = true;
    };
    // Deps: orgId + rolesKey (a stable join of the role list) + reloadNonce.
    // `roles` itself is intentionally not a dep — rolesKey stands in for it so
    // the effect only refetches when the actual role set changes.
  }, [orgId, rolesKey, reloadNonce]);

  const value = useMemo<PermissionContextValue>(() => {
    const ready = state.status === 'ready';
    const set = state.permissions;
    return {
      status: state.status,
      permissions: set,
      loadedRoles: state.loadedRoles,
      missingRoles: state.missingRoles,
      error: state.error,
      hasAccess: ready && hasAnyAccessibleModule(set),
      // Gate helpers fail closed until permissions are ready.
      canAccess: (req) => ready && canAccess(set, req),
      canView: (m, s) => ready && canViewSet(set, m, s),
      canEdit: (m, s) => ready && canEditSet(set, m, s),
      canFull: (m, s) => ready && canFullSet(set, m, s),
      reload: () => setReloadNonce((n) => n + 1),
    };
  }, [state]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return ctx;
}

/** Convenience hook: does the current user satisfy this requirement? */
export function useCanAccess(req: PermissionRequirement): boolean {
  return usePermissions().canAccess(req);
}

/** Log the resolved permissions after login (module|subModule -> access). */
function logPermissions(
  orgId: string,
  roles: string[],
  result: { permissions: PermissionSet; loadedRoles: string[]; missingRoles: string[] },
): void {
  const entries = Object.entries(result.permissions).sort(([a], [b]) => a.localeCompare(b));
  const summary =
    `[RBAC] Permissions for org "${orgId}" — roles: [${roles.join(', ')}]` +
    (result.missingRoles.length ? ` (missing role docs: ${result.missingRoles.join(', ')})` : '') +
    ` — ${entries.length} permission(s)`;

  console.log(summary);
  if (typeof console.table === 'function') {
    console.table(Object.fromEntries(entries));
  } else {
    console.log(Object.fromEntries(entries));
  }
}
